import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  agreementTexts,
  buttonName,
  commonErrMsg,
  commonValidationMsg,
  tooltips,
  uploadDoc
} from "../../locales/en";
import { FaBackward, FaSpinner } from "react-icons/fa";
import AgreementModal from "../../components/modals/AgreementModal";
import { downloadFileFromResponse, showToast } from "../../utils/utils";
import privateAxios from "../../utils/axiosPrivate";
import {
  getAccAnnatureStatusURL,
  getAgreementDownloadUrl,
  getAgreementUploadUrl,
  getGenerateEmailUrl
} from "../../utils/constants/apiEndPoints";
import {
  CALL_NO_NZ,
  COMPANY_EMAIL,
  FILE_SIZE_LIMIT,
  INFO_EMAIL,
  INITIATED,
  MAILTO_COMPANY_EMAIL,
  showToastType
} from "../../utils/constants/appConstants";
import { Link, useNavigate } from "react-router-dom";
import { MY_ACCOUNT } from "../../route/paths";
import PropTypes from "prop-types";

const UploadDocument = ({ onboardingStatus }) => {
  const navigate = useNavigate();
  const [showManually, setShowManually] = useState(false);
  const [addAgreementModal, setAddAgreementModal] = useState(false);
  const [isDisabledButton, setIsDisabledButton] = useState(false);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [annatureStatusData, setAnnatureStatusData] = useState(null);

  const [anatureDataUrl, setAnatureDataUrl] = useState(null);
  const [anatureLoading, setAnatureLoading] = useState(false);
  const [anatureError, setAnatureError] = useState(null);

  const fileInput = useRef(null);
  const hasRequestedAgreementRef = useRef(false);
  const toggleShowManually = () => {
    setShowManually(!showManually);
  };
  const handleBackToDigitalClick = () => {
    setShowManually(false);
  };

  const showAgreementModal = () => {
    setAddAgreementModal(!addAgreementModal);
  };

  const handleDownload = async () => {
    try {
      setLoading(true);
      const response = await privateAxios.get(getAgreementDownloadUrl, {
        responseType: "arraybuffer"
      });
      downloadFileFromResponse(response);
    } catch {
      showToast(commonErrMsg?.item11, showToastType.error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (file.size > FILE_SIZE_LIMIT) {
      showToast(commonValidationMsg?.fileLimitError, showToastType.error);
      event.target.value = "";
      setFileName("");
      setSelectedFile(null);
      return;
    }

    setFileName(file.name);
    setSelectedFile(file);
  };

  const handleUploadButtonClick = async () => {
    if (!selectedFile) {
      showToast(commonValidationMsg?.fileUpload, showToastType.error);
      return;
    }

    try {
      setLoadingUpload(true);

      const formDataToSend = new FormData();
      formDataToSend.append("file", selectedFile, selectedFile.name);

      const response = await privateAxios.post(
        getAgreementUploadUrl,
        formDataToSend,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.status === 201) {
        showToast(response.data.message, showToastType.success);
        setFileName("");
        setSelectedFile(null);
        setShowSuccessMessage(true);
        navigate(MY_ACCOUNT);
      }
    } catch (error) {
      if (error.response) {
        showToast(error.response.data.message, showToastType.error);
      }
    } finally {
      setLoadingUpload(false);
    }
  };

  const openFileDialog = () => {
    if (fileInput.current) {
      fileInput.current.click();
    }
  };

  const handleAnnatureCheckStatus = useCallback(async (openModal = false) => {
    try {
      const response = await privateAxios.post(getAccAnnatureStatusURL);
      if (response.status === 200) {
        const responseData = response.data.data;
        setAnnatureStatusData(responseData);
        if (openModal && !responseData?.status) {
          setAddAgreementModal(true);
        }
      }
    } catch (error) {
      showToast(error.response.data.message, showToastType.error);
    }
  }, []);
  useEffect(() => {
    handleAnnatureCheckStatus();
  }, [handleAnnatureCheckStatus]);

  const handleClickAgreement = () => {
    handleAnnatureCheckStatus(true);
  };

  // Generating the agreement creates and sends a new Annature envelope, which
  // emails the customer. Wait for the status check, skip it once the agreement
  // is signed, and generate at most once per page load.
  useEffect(() => {
    if (
      onboardingStatus !== INITIATED ||
      !annatureStatusData ||
      annatureStatusData.status ||
      hasRequestedAgreementRef.current
    ) {
      return;
    }

    const handleOpenAnaturePdf = async () => {
      try {
        setAnatureLoading(true);
        const response = await privateAxios.post(getGenerateEmailUrl);
        setAnatureDataUrl(response.data.data.signingToken);
      } catch {
        setAnatureError(commonErrMsg?.failedDocumentCreate);
      } finally {
        setAnatureLoading(false);
      }
    };

    hasRequestedAgreementRef.current = true;
    handleOpenAnaturePdf();
  }, [onboardingStatus, annatureStatusData]);

  const agreementProps = {
    isOpen: addAgreementModal,
    toggle: showAgreementModal,
    handleAnnatureCheckStatus,
    setIsDisabledButton,
    anatureDataUrl,
    loading: anatureLoading,
    error: anatureError
  };

  return (
    <>
      <div className="step-wrap">
        <div className="step-content mt-3">
          <div className={`${showManually ? "hide" : "show"} hide-manually`}>
            {annatureStatusData && annatureStatusData.status ? (
              <div className="aplyid-section">
                <p className="text-success">
                  {annatureStatusData?.displayText}
                </p>
              </div>
            ) : (
              <>
                <div className="aplyid-section">
                  <p>{uploadDoc?.note}</p>
                </div>

                <div className="agreement-btn-group mb-3">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleClickAgreement}
                    disabled={isDisabledButton}
                  >
                    {agreementTexts?.reviewSign}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-transparent"
                    onClick={toggleShowManually}
                    disabled={isDisabledButton}
                  >
                    {agreementTexts?.signUpload}
                  </button>
                </div>
              </>
            )}
          </div>
          {showManually && (
            <div className="show-manually-wrap mt-5">
              {showSuccessMessage ? (
                <div className="aplyid-section">
                  <p>
                    {agreementTexts?.uploadDocsSuccessText}
                    {` ${INFO_EMAIL} `}
                  </p>
                </div>
              ) : (
                <>
                  <div className="show-manual-btn mb-5">
                    <button
                      type="button"
                      className="btn btn-primary btn-transparent"
                      onClick={handleBackToDigitalClick}
                    >
                      <FaBackward className="me-2" />
                      {agreementTexts?.reviewSign}
                    </button>
                  </div>
                  <div className="aplyid-section">
                    <p>{agreementTexts?.uploadDocsText}</p>
                    <p>{agreementTexts?.uploadDocsText_1}</p>
                    <p>
                      <Link to={MAILTO_COMPANY_EMAIL}>{COMPANY_EMAIL}</Link>
                      {` ${agreementTexts?.uploadDocsText_2} `}
                      {CALL_NO_NZ}
                    </p>

                    <div className="agreement-group-warp mt-5">
                      <div className="row mb-2">
                        <div className="col-md-6 d-flex pt-2 pb-2 align-items-center">
                          <label htmlFor="Download">
                            {agreementTexts?.downloadAgreement}
                          </label>
                        </div>
                        <div className="col-md-6">
                          <button
                            className={`btn btn-primary fundtap_agreement spinDiv ${loading ? "spinDivBlack" : ""}`}
                            id="Download"
                            onClick={handleDownload}
                          >
                            {buttonName.download}
                            {loading && (
                              <span className="loadSpin">
                                <FaSpinner />
                              </span>
                            )}
                          </button>
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col-md-6 d-flex pt-2 pb-2 align-items-center">
                          <label htmlFor="uploadAgreement">
                            {agreementTexts?.uploadAgreement}
                          </label>
                        </div>
                        <div className="col-md-6">
                          <div className="upload-file">
                            {fileName && (
                              <p className="uploaded-file-name">{fileName}</p>
                            )}
                            <button
                              type="button"
                              className={`btn btn-primary fundtap_agreement spinDiv`}
                              onClick={openFileDialog}
                            >
                              {tooltips.upload}
                            </button>
                            <input
                              name="isFile"
                              type="file"
                              ref={fileInput}
                              onChange={handleFileChange}
                              className="d-none"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-6 d-flex pt-2 pb-2 align-items-center" />
                        <div className="col-md-6">
                          <div className="upload-file">
                            <button
                              type="button"
                              onClick={handleUploadButtonClick}
                              className={`btn btn-primary fundtap_agreement spinDiv ${loadingUpload ? "spinDivBlack" : ""}`}
                            >
                              {buttonName.submit}
                              {loadingUpload && (
                                <span className="loadSpin">
                                  <FaSpinner />
                                </span>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <AgreementModal {...agreementProps} />
    </>
  );
};

UploadDocument.propTypes = {
  onboardingStatus: PropTypes.string
};

export default UploadDocument;
